
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { PaymentGatewayContent } from './PaymentGatewayContent';
import { PaymentMethodType, PaymentContentType } from '../../types/payment';

interface PaymentGatewayCardProps {
  showPaymentOptions: boolean;
  showPaymentForm: boolean;
  paymentType: PaymentContentType;
  amount: number;
  paymentMethod: PaymentMethodType;
  isConfirming: boolean;
  isProcessing: boolean;
  onPayNow: () => void;
  onBankTransfer: () => void;
  onPaymentMethodChange: (method: PaymentMethodType) => void;
  onCancel: () => void;
  onConfirmPayment: () => void;
}

const PaymentGatewayCard = ({
  showPaymentOptions,
  showPaymentForm,
  paymentType,
  amount,
  paymentMethod,
  isConfirming,
  isProcessing,
  onPayNow,
  onBankTransfer,
  onPaymentMethodChange,
  onCancel,
  onConfirmPayment
}: PaymentGatewayCardProps) => {
  const { t, language } = useLanguage();
  const [isBankTransferReady, setIsBankTransferReady] = useState(false);

  // Determine if confirm button should be disabled
  const isConfirmDisabled = isProcessing || isConfirming || (paymentMethod === 'bank_transfer' && !isBankTransferReady);

  return (
    <Card className={`w-full max-w-2xl mx-auto border-0 shadow-lg bg-gradient-to-br from-background to-muted/20 ${language === 'ar' ? 'text-right' : ''}`}>
      <CardHeader className="pb-6">
        <div className="text-center space-y-2">
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
            {t('completePayment')}
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            {t('choosePaymentMethod')}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <PaymentGatewayContent
          showPaymentOptions={showPaymentOptions}
          showPaymentForm={showPaymentForm}
          paymentType={paymentType}
          amount={amount}
          onPayNow={onPayNow}
          onBankTransfer={onBankTransfer}
          onPaymentMethodChange={onPaymentMethodChange}
          paymentMethod={paymentMethod}
          onBankTransferReadyChange={setIsBankTransferReady}
        />
      </CardContent>
      <CardFooter className="flex justify-center gap-4 pt-6 border-t border-border/50">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing || isConfirming}
          className="min-w-[100px]"
        >
          {t('cancel')}
        </Button>
        {showPaymentForm && (
          <Button
            onClick={onConfirmPayment}
            disabled={isConfirmDisabled}
            className="min-w-[140px] bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80"
          >
            {isConfirming ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                {language === 'ar' ? 'جارٍ التأكيد...' : 'Confirming...'}
              </div>
            ) : (
              language === 'ar' ? 'تأكيد الدفع' : 'Confirm Payment'
            )}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default PaymentGatewayCard;
